import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Form, Select, DatePicker, InputNumber, Button, Space, Row, Col, Badge, Statistic, Divider, message } from 'antd';
import { SearchOutlined, CalendarOutlined, FilterOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/http';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { Item } = Form;

const LedgerList = () => {
  const [form] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [statistics, setStatistics] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [overtimeTypes, setOvertimeTypes] = useState([]);
  const [searchParams, setSearchParams] = useState({
    record_type: '',
    employee_id: '',
    department: '',
    section: '',
    start_date: '',
    end_date: '',
    status: '',
    type: '',
    page: 1,
    page_size: 10,
    order_by: 'date',
    order: 'DESC'
  });

  // 状态标签配置
  const statusConfig = {
    PENDING: { text: '待审批', color: 'processing' },
    APPROVED: { text: '已批准', color: 'success' },
    REJECTED: { text: '已拒绝', color: 'error' }
  };

  // 初始化数据
  useEffect(() => {
    fetchInitialData();
    fetchLedgerData();
  }, []);

  // 获取初始配置数据
  const fetchInitialData = async () => {
    try {
      // 获取部门列表 - 添加错误处理和类型检查
      try {
        const deptResponse = await api.get('/departments');
        if (deptResponse && Array.isArray(deptResponse.data)) {
          const mappedDepartments = deptResponse.data
            .map(item => ({
              id: item.id,
              name: item.name || item.department || item.label || ''
            }))
            .filter(item => item.name);
          setDepartments(mappedDepartments);
        } else {
          console.warn('部门列表数据格式不正确，设置为空数组');
          setDepartments([]);
        }
      } catch (deptError) {
        console.error('获取部门列表失败:', deptError);
        setDepartments([]);
      }

      // 获取请假类型 - 添加错误处理和类型检查
      try {
        const leaveConfigResponse = await api.get('/system-configs?type=LEAVE_TYPE');
        // 确保数据是数组类型
        const leaveConfigData = Array.isArray(leaveConfigResponse?.data) ? leaveConfigResponse.data : [];
        const leaveTypesData = leaveConfigData.map(item => ({
          label: item?.value || '',
          value: item?.key || ''
        }));
        setLeaveTypes(leaveTypesData);
      } catch (leaveError) {
        console.error('获取请假类型失败:', leaveError);
        setLeaveTypes([]);
      }

      // 获取加班类型 - 添加错误处理和类型检查
      try {
        const overtimeConfigResponse = await api.get('/system-configs?type=OVERTIME_TYPE');
        // 确保数据是数组类型
        const overtimeConfigData = Array.isArray(overtimeConfigResponse?.data) ? overtimeConfigResponse.data : [];
        const overtimeTypesData = overtimeConfigData.map(item => ({
          label: item?.value || '',
          value: item?.key || ''
        }));
        setOvertimeTypes(overtimeTypesData);
      } catch (overtimeError) {
        console.error('获取加班类型失败:', overtimeError);
        setOvertimeTypes([]);
      }
    } catch (error) {
      console.error('获取初始数据失败:', error);
      message.error('获取初始数据失败');
    }
  };

  // 获取台账数据
  const fetchLedgerData = async () => {
    try {
      setLoading(true);
      
      // 构建查询参数
      const params = { ...searchParams };
      
      // 处理日期范围，添加安全检查
      if (params.date_range && Array.isArray(params.date_range) && params.date_range.length === 2) {
        params.start_date = params.date_range[0]?.format?.('YYYY-MM-DD') || '';
        params.end_date = params.date_range[1]?.format?.('YYYY-MM-DD') || '';
        delete params.date_range;
      }

      const response = await api.get('/leave-overtime/ledger', { params });
      
      // 确保response.data存在且结构正确
      if (!response || !response.data) {
        throw new Error('API返回数据结构不正确');
      }
      
      // 安全地获取data和pagination属性
      const responseData = Array.isArray(response.data.data) ? response.data.data : [];
      const responsePagination = response.data.pagination || {};
      
      // 设置数据源，确保是数组
      setDataSource(responseData);
      
      // 设置分页信息，添加类型检查
      setPagination({
        current: typeof responsePagination.page === 'number' ? responsePagination.page : 1,
        pageSize: typeof responsePagination.page_size === 'number' ? responsePagination.page_size : 10,
        total: typeof responsePagination.total === 'number' ? responsePagination.total : 0
      });

      // 获取统计数据
      fetchStatistics();
    } catch (error) {
      console.error('获取台账数据失败:', error);
      message.error('获取台账数据失败');
      // 出错时设置空数据，避免UI显示异常
      setDataSource([]);
    } finally {
      setLoading(false);
    }
  };

  // 获取统计数据
  const fetchStatistics = async () => {
    try {
      const params = { ...searchParams };
      
      // 处理日期范围
      if (params.date_range && params.date_range.length === 2) {
        params.start_date = params.date_range[0].format('YYYY-MM-DD');
        params.end_date = params.date_range[1].format('YYYY-MM-DD');
        delete params.date_range;
      }

      // 移除分页参数
      delete params.page;
      delete params.page_size;
      delete params.order_by;
      delete params.order;

      const response = await api.get('/leave-overtime/ledger/stats', { params });
      if (response && response.data) {
        setStatistics(response.data.data);
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
    }
  };

  // 处理搜索
  const handleSearch = (values) => {
    const newParams = { ...searchParams, ...values, page: 1 };
    setSearchParams(newParams);
    fetchLedgerData(newParams);
  };

  // 处理重置
  const handleReset = () => {
    form.resetFields();
    const defaultParams = {
      record_type: '',
      employee_id: '',
      department: '',
      section: '',
      start_date: '',
      end_date: '',
      status: '',
      type: '',
      page: 1,
      page_size: 10,
      order_by: 'date',
      order: 'DESC'
    };
    setSearchParams(defaultParams);
    fetchLedgerData(defaultParams);
  };

  // 处理分页变化
  const handlePaginationChange = (page, pageSize) => {
    const newParams = { ...searchParams, page, page_size: pageSize };
    setSearchParams(newParams);
    fetchLedgerData(newParams);
  };

  // 处理排序变化
  const handleTableChange = (pagination, filters, sorter) => {
    const newParams = { ...searchParams };
    
    if (sorter.field) {
      newParams.order_by = sorter.field;
      newParams.order = sorter.order === 'ascend' ? 'ASC' : 'DESC';
    }
    
    setSearchParams(newParams);
    fetchLedgerData(newParams);
  };

  // 处理导出
  const handleExport = async () => {
    try {
      setLoading(true);
      
      const params = { ...searchParams, export: true };
      
      // 处理日期范围
      if (params.date_range && params.date_range.length === 2) {
        params.start_date = params.date_range[0].format('YYYY-MM-DD');
        params.end_date = params.date_range[1].format('YYYY-MM-DD');
        delete params.date_range;
      }

      // 调用导出接口
      const response = await api.get('/leave-overtime/export/ledger', {
        params,
        responseType: 'blob'
      });
      
      // 处理导出结果
      if (response) {
        const blob = new Blob([response], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `leave-overtime-ledger-${dayjs().format('YYYY-MM-DD')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success('导出数据成功');
      }
    } catch (error) {
      console.error('导出数据失败:', error);
      message.error('导出数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 根据记录类型获取类型列表
  const getTypeOptions = useMemo(() => {
    const recordType = searchParams.record_type;
    if (recordType === 'LEAVE') {
      return leaveTypes;
    } else if (recordType === 'OVERTIME') {
      return overtimeTypes;
    }
    return [...leaveTypes, ...overtimeTypes];
  }, [searchParams.record_type, leaveTypes, overtimeTypes]);

  // 列配置
  const columns = [
    {
      title: '记录类型',
      dataIndex: 'record_type',
      key: 'record_type',
      render: (type) => {
        return type === 'LEAVE' ? '请假' : '加班';
      },
      filters: [
        { text: '请假', value: 'LEAVE' },
        { text: '加班', value: 'OVERTIME' }
      ],
      onFilter: (value, record) => record.record_type === value
    },
    {
      title: '申请人',
      dataIndex: 'employee_name',
      key: 'employee_name'
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
      filters: departments.map(dept => ({ text: dept.name, value: dept.name })),
      onFilter: (value, record) => (record.department || '') === value
    },
    {
      title: '类型',
      dataIndex: 'full_type_name',
      key: 'full_type_name'
    },
    {
      title: '日期',
      dataIndex: 'date',
      key: 'date',
      sorter: true,
      render: (date) => {
        if (!date) return '-';
        const normalized = dayjs(date);
        return normalized.isValid() ? normalized.format('YYYY-MM-DD') : date;
      }
    },
    {
      title: '时长(小时)',
      dataIndex: 'hours',
      key: 'hours',
      sorter: true
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Badge 
          status={statusConfig[status]?.color || 'default'} 
          text={statusConfig[status]?.text || status} 
        />
      ),
      filters: [
        { text: '待审批', value: 'PENDING' },
        { text: '已批准', value: 'APPROVED' },
        { text: '已拒绝', value: 'REJECTED' }
      ],
      onFilter: (value, record) => record.status === value
    },
    {
      title: '申请时间',
      dataIndex: 'apply_time',
      key: 'apply_time',
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm') : '-',
      sorter: true
    },
    {
      title: '原因',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (reason) => <span title={reason}>{reason}</span>
    }
  ];

  return (
    <Card title="请假加班台账" className="ledger-list" extra={<Button type="primary" onClick={handleExport} icon={<DownloadOutlined />}>导出数据</Button>}>
      {/* 统计数据区域 */}
      {statistics && (
        <div className="statistics-section" style={{ marginBottom: 20 }}>
          <Row gutter={16}>
            <Col span={6}>
              <Card bordered={false}>
                <Statistic 
                  title="总记录数" 
                  value={statistics.summary.total_records} 
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card bordered={false}>
                <Statistic 
                  title="请假总时长" 
                  value={statistics.summary.total_leave_hours} 
                  suffix="小时"
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card bordered={false}>
                <Statistic 
                  title="加班总时长" 
                  value={statistics.summary.total_overtime_hours} 
                  suffix="小时"
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card bordered={false}>
                <Statistic 
                  title="涉及员工数" 
                  value={statistics.summary.unique_employees} 
                />
              </Card>
            </Col>
          </Row>
          <Divider />
        </div>
      )}

      {/* 筛选条件 */}
      <Form
        form={form}
        layout="horizontal"
        className="search-form"
        onFinish={handleSearch}
        initialValues={searchParams}
      >
        <Row gutter={16}>
          <Col span={4}>
            <Item name="record_type" label="记录类型">
              <Select placeholder="请选择记录类型" onChange={(value) => form.setFieldValue('type', '')}>
                <Option value="">全部</Option>
                <Option value="LEAVE">请假</Option>
                <Option value="OVERTIME">加班</Option>
              </Select>
            </Item>
          </Col>
          <Col span={4}>
            <Item name="type" label="类型">
              <Select placeholder="请选择类型">
                <Option value="">全部</Option>
                {getTypeOptions.map(type => (
                  <Option key={type.value} value={type.value}>{type.label}</Option>
                ))}
              </Select>
            </Item>
          </Col>
          <Col span={4}>
            <Item name="department" label="部门">
              <Select placeholder="请选择部门">
                <Option value="">全部</Option>
                {departments.map(dept => (
                  <Option key={dept.id || dept.name} value={dept.name}>
                    {dept.name}
                  </Option>
                ))}
              </Select>
            </Item>
          </Col>
          <Col span={4}>
            <Item name="status" label="状态">
              <Select placeholder="请选择状态">
                <Option value="">全部</Option>
                <Option value="PENDING">待审批</Option>
                <Option value="APPROVED">已批准</Option>
                <Option value="REJECTED">已拒绝</Option>
              </Select>
            </Item>
          </Col>
          <Col span={8}>
            <Item name="date_range" label="日期范围" getValueFromEvent={(dates) => dates}>
              <RangePicker style={{ width: '100%' }} placeholder={['开始日期', '结束日期']} />
            </Item>
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Space>
              <Button onClick={handleReset}>重置</Button>
              <Button type="primary" htmlType="submit" loading={loading}>
                查询
              </Button>
              {/* 导出按钮已移至卡片标题栏 */}
              <Button onClick={fetchLedgerData} icon={<ReloadOutlined />} loading={loading}>
                刷新
              </Button>
            </Space>
          </Col>
        </Row>
      </Form>

      {/* 数据表格 */}
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="id"
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1400 }}
        className="ledger-table"
      />
    </Card>
  );
};

export default LedgerList;